import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, FileDown, Car, Calendar, Gauge, Fuel, Award, ExternalLink,
} from 'lucide-react';
import { API, API_BASE } from '../lib/api';

const inr = (v) => (typeof v === 'number' ? `₹ ${v.toLocaleString('en-IN')}` : v);

const gradeStyle = {
  Excellent: { bg: 'bg-emerald-500', text: 'text-emerald-50', ring: 'ring-emerald-500/30' },
  Good: { bg: 'bg-teal-500', text: 'text-teal-50', ring: 'ring-teal-500/30' },
  Average: { bg: 'bg-amber-500', text: 'text-amber-50', ring: 'ring-amber-500/30' },
  Poor: { bg: 'bg-red-500', text: 'text-red-50', ring: 'ring-red-500/30' },
  Pending: { bg: 'bg-slate-500', text: 'text-slate-50', ring: 'ring-slate-500/30' },
};

const gradeHelper = (g) => ({
  Excellent: 'Go ahead and buy it',
  Good: 'Solid pick — minor fixes only',
  Average: 'Negotiate — needs work',
  Poor: 'Buy with caution or walk away',
  Pending: 'Inspection in progress',
}[g] || '—');

const PublicInspection = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/public/inspections/${token}`);
        setData(d);
      } catch (e) {
        setError(e.response?.status === 404
          ? 'This inspection report is not available.'
          : e.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/public/inspections/${token}/pdf`, {
        responseType: 'blob', timeout: 60000,
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `UsedCarInspection_${data?.booking_id || token.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 800);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500" data-testid="public-inspection-loading">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading report…
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center text-slate-600 px-4" data-testid="public-inspection-error">
        <div className="w-14 h-14 rounded-full bg-slate-200 flex items-center justify-center mb-4">
          <Car className="text-slate-400" size={22} />
        </div>
        <h1 className="text-xl font-semibold text-slate-900 mb-1">Report not available</h1>
        <p className="text-sm text-slate-500 max-w-md text-center">{error}</p>
        <Link to="/marketplace" className="mt-6 text-teal-700 hover:text-teal-600 text-sm font-medium">
          Browse pre-owned cars →
        </Link>
      </div>
    );
  }

  const grade = data.overall_grade || 'Pending';
  const gs = gradeStyle[grade] || gradeStyle.Pending;
  const title = [data.make, data.model, data.variant].filter(Boolean).join(' ');
  const sectionScores = data.section_scores || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-inspection-page">
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-teal-600 text-white flex items-center justify-center font-bold text-sm">U</div>
            <div className="text-slate-900 font-semibold text-sm">Unisol Global</div>
          </Link>
          <div className="text-[11px] uppercase tracking-widest text-slate-400">
            Inspection Report
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Vehicle header */}
        <p className="text-xs text-teal-700 font-semibold uppercase tracking-wider" data-testid="public-inspection-variant">
          {data.variant || 'Pre-owned car'}
        </p>
        <h1 className="text-2xl sm:text-4xl font-bold text-slate-900 mt-1" data-testid="public-inspection-title">
          {title || '—'}
        </h1>
        <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-slate-500">
          {data.manufacture_year && <span className="inline-flex items-center gap-1"><Calendar size={12} /> {data.manufacture_year}</span>}
          {data.km_driven && <span className="inline-flex items-center gap-1"><Gauge size={12} /> {data.km_driven.toLocaleString('en-IN')} km</span>}
          {data.fuel_type && <span className="inline-flex items-center gap-1"><Fuel size={12} /> {data.fuel_type}</span>}
          {data.registration_number && <span className="inline-flex items-center gap-1 font-mono text-teal-700">{data.registration_number}</span>}
        </div>

        {/* Grade card */}
        <div className={`${gs.bg} ${gs.text} rounded-2xl p-6 mt-6 shadow-xl ring-8 ${gs.ring}`} data-testid="public-grade-card">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest opacity-80 flex items-center gap-1"><Award size={12} /> Overall Grade</div>
              <div className="text-4xl sm:text-5xl font-bold mt-1" data-testid="public-grade-value">{grade}</div>
              <div className="text-sm opacity-90 mt-1">{gradeHelper(grade)}</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest opacity-80">Score</div>
              <div className="text-4xl sm:text-5xl font-bold" data-testid="public-score-value">
                {typeof data.overall_score === 'number' ? data.overall_score.toFixed(1) : '—'}
                <span className="text-xl opacity-70">/100</span>
              </div>
              {data.booking_id && (
                <div className="text-[11px] opacity-80 mt-1 font-mono tracking-wider">{data.booking_id}</div>
              )}
            </div>
          </div>
        </div>

        {/* Download CTA */}
        <div className="mt-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <button
            onClick={downloadPdf}
            disabled={downloading}
            className="inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm px-5 py-3 rounded-xl transition disabled:opacity-60"
            data-testid="public-download-pdf"
          >
            {downloading ? <Loader2 className="animate-spin" size={16} /> : <FileDown size={16} />}
            Download full inspection PDF
          </button>
          <a
            href={`${API_BASE}/api/public/inspections/${token}/pdf`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-100 border border-slate-300 text-slate-700 font-semibold text-sm px-5 py-3 rounded-xl transition"
            data-testid="public-view-pdf"
          >
            <ExternalLink size={14} /> View in new tab
          </a>
        </div>

        {/* Section scores */}
        {Object.keys(sectionScores).length > 0 && (
          <section className="mt-8" data-testid="public-section-scores">
            <h2 className="text-sm uppercase tracking-widest text-slate-500 font-semibold mb-3">Section Breakdown</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.entries(sectionScores).map(([slug, score]) => (
                <div key={slug} className="bg-white border border-slate-200 rounded-xl px-4 py-3" data-testid={`public-section-${slug}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-700 capitalize">{slug.replace(/_/g, ' ')}</span>
                    <span className={`text-sm font-semibold ${score >= 70 ? 'text-emerald-600' : score >= 55 ? 'text-amber-600' : 'text-red-600'}`}>
                      {typeof score === 'number' ? score.toFixed(0) : '—'} / 100
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${score >= 70 ? 'bg-emerald-500' : score >= 55 ? 'bg-amber-500' : 'bg-red-500'}`}
                      style={{ width: `${Math.min(100, Math.max(0, score))}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Valuation */}
        {(data.valuation_min || data.valuation_max) && (
          <section className="mt-8" data-testid="public-valuation">
            <h2 className="text-sm uppercase tracking-widest text-slate-500 font-semibold mb-3">Estimated Fair Market Value</h2>
            <div className="bg-teal-50 border border-teal-200 rounded-2xl p-6 text-center">
              <div className="text-3xl sm:text-4xl font-bold text-teal-800">
                {data.valuation_min && data.valuation_max
                  ? `${inr(data.valuation_min)} – ${inr(data.valuation_max)}`
                  : inr(data.valuation_min || data.valuation_max)}
              </div>
              {data.valuation_notes && (
                <p className="text-sm text-teal-900/80 mt-2 max-w-lg mx-auto">{data.valuation_notes}</p>
              )}
            </div>
          </section>
        )}

        {/* Outstanding fines (buyer visible) */}
        {data.outstanding_fines_notes && (
          <section className="mt-8" data-testid="public-fines">
            <h2 className="text-sm uppercase tracking-widest text-slate-500 font-semibold mb-3">
              Outstanding Traffic Fines / e-Challans
            </h2>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 text-sm text-amber-900 whitespace-pre-line">
              {data.outstanding_fines_notes}
            </div>
          </section>
        )}

        {/* Service history (Surepass) */}
        {data.service_history_records?.length > 0 && (
          <section className="mt-8" data-testid="public-service-history">
            <h2 className="text-sm uppercase tracking-widest text-slate-500 font-semibold mb-3">
              Dealer Service History
            </h2>
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <table className="w-full text-xs sm:text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Dealer</th>
                    <th className="text-right px-3 py-2">Odometer</th>
                    <th className="text-left px-3 py-2 hidden sm:table-cell">Service</th>
                    <th className="text-right px-3 py-2">Amount</th>
                  </tr>
                </thead>
                <tbody className="text-slate-700">
                  {data.service_history_records.map((r, i) => (
                    <tr key={i} className={i % 2 ? 'bg-slate-50/40' : ''}>
                      <td className="px-3 py-2">{r.service_date || '—'}</td>
                      <td className="px-3 py-2">
                        {r.dealer_name || '—'}
                        {r.dealer_location && <div className="text-[11px] text-slate-500">{r.dealer_location}</div>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {r.odometer != null ? `${r.odometer.toLocaleString('en-IN')} km` : '—'}
                      </td>
                      <td className="px-3 py-2 hidden sm:table-cell">{r.service_type || '—'}</td>
                      <td className="px-3 py-2 text-right font-semibold text-teal-700">
                        {r.invoice_amount != null ? `₹ ${r.invoice_amount.toLocaleString('en-IN')}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-slate-400 mt-2">
              Sourced from OEM-authorised dealer networks via Surepass. Independent workshop history may not appear.
            </p>
          </section>
        )}

        {/* Trust footer */}
        <section className="mt-10 bg-white border border-slate-200 rounded-2xl p-6 text-center">
          <p className="text-slate-700 text-sm leading-relaxed">
            This inspection was performed by <strong>{data.inspector_name || 'Unisol Global'}</strong>
            {data.inspection_date ? ` on ${new Date(data.inspection_date).toLocaleDateString()}` : ''}
            {data.inspection_location ? ` at ${data.inspection_location}` : ''}.
          </p>
          <p className="text-xs text-slate-500 mt-2">
            Want your car checked by Unisol before you buy or sell?
          </p>
          <Link
            to="/marketplace"
            className="inline-flex items-center gap-1 mt-3 text-teal-700 hover:text-teal-600 text-sm font-semibold"
            data-testid="public-browse-marketplace"
          >
            Browse pre-owned cars on Unisol →
          </Link>
        </section>

        <footer className="mt-10 text-center text-[11px] text-slate-400">
          © {new Date().getFullYear()} Unisol Global. Report is a professional opinion, not a warranty on the vehicle.
        </footer>
      </main>
    </div>
  );
};

export default PublicInspection;
